import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../../components/common/PageHeader';
import { FaUserTie, FaChartPie, FaDollarSign, FaCog } from 'react-icons/fa';

const ManagedPortfoliosPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Services', link: '/services' },
    { label: 'Managed Portfolios' }
  ];

  const portfolioFeatures = [
    {
      icon: <FaUserTie className="w-8 h-8 text-primary" />,
      title: "Professional Management",
      description: "Expert portfolio managers handle all investment decisions and ongoing management of your assets."
    },
    {
      icon: <FaChartPie className="w-8 h-8 text-primary" />,
      title: "Diversified Holdings",
      description: "Broadly diversified portfolios of mutual funds and exchange-traded funds (ETFs) to spread risk."
    },
    {
      icon: <FaDollarSign className="w-8 h-8 text-primary" />,
      title: "Competitive Fees",
      description: "Transparent, competitive fee structure with low minimums to make professional management accessible."
    },
    {
      icon: <FaCog className="w-8 h-8 text-primary" />,
      title: "Automatic Rebalancing",
      description: "Regular portfolio rebalancing to maintain optimal asset allocation and risk levels."
    }
  ];

  const portfolioTypes = [
    {
      title: "Conservative Portfolio",
      description: "Lower risk approach focusing on capital preservation and steady income generation.",
      allocation: "20% Stocks, 80% Bonds"
    },
    {
      title: "Moderate Portfolio",
      description: "Balanced approach seeking growth while managing risk through diversification.",
      allocation: "60% Stocks, 40% Bonds"
    },
    {
      title: "Aggressive Portfolio",
      description: "Growth-focused strategy for investors with higher risk tolerance and longer time horizons.",
      allocation: "80% Stocks, 20% Bonds"
    }
  ];

  return (
    <>
      <PageHeader 
        title="Managed Portfolios" 
        breadcrumbs={breadcrumbs}
        backgroundImage="/assets/business-colleagues-banner-concept-with-copy-space.jpg"
      />
      
      <section className="py-16 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-heading font-bold text-primary mb-6">
                Professional Portfolio Management
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                <span className="font-semibold">Blockfirst Capital</span> Managed Portfolios provide professionally managed, broadly diversified 
                portfolios of mutual funds or exchange-traded funds (ETFs) with competitive fees and minimums. 
                Our expert team handles all the complex investment decisions, so you can focus on what matters most to you.
              </p>
              <p className="text-lg text-gray-700">
                Whether you're looking for conservative income generation, balanced growth, or aggressive capital 
                appreciation, we have portfolio solutions designed to meet your specific investment objectives 
                and risk tolerance.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/person-office-analyzing-checking-finance-graphs.jpg" 
                alt="Portfolio Management Analysis" 
                className="rounded-xl shadow-custom w-full h-full object-cover"
              />
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {portfolioFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-custom hover:shadow-custom-lg transition-all duration-300"
              >
                <div className="bg-gray-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-heading font-semibold mb-4 text-primary">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-8 md:p-12 shadow-custom mb-16"
          >
            <h3 className="text-3xl font-heading font-bold text-primary mb-8 text-center">
              Portfolio Options
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {portfolioTypes.map((portfolio, index) => (
                <div key={index} className="text-center p-6 border border-gray-200 rounded-lg">
                  <h4 className="text-xl font-semibold text-primary mb-4">{portfolio.title}</h4>
                  <p className="text-gray-600 mb-4">{portfolio.description}</p>
                  <div className="bg-gray-100 p-4 rounded-lg">
                    <p className="font-semibold text-primary">{portfolio.allocation}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-8 md:p-12 shadow-custom mb-16"
          >
            <h3 className="text-3xl font-heading font-bold text-primary mb-6 text-center">
              Why Choose Managed Portfolios?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h4 className="text-xl font-semibold text-primary">Professional Expertise</h4>
                <p className="text-gray-600">
                  Our investment professionals bring decades of experience and advanced analytical tools 
                  to manage your portfolio with institutional-quality investment processes.
                </p>
              </div>
              <div className="space-y-4">
                <h4 className="text-xl font-semibold text-primary">Cost Efficiency</h4>
                <p className="text-gray-600">
                  Lower fees than many actively managed funds while providing professional oversight 
                  and strategic asset allocation adjustments.
                </p>
              </div>
              <div className="space-y-4">
                <h4 className="text-xl font-semibold text-primary">Time Savings</h4>
                <p className="text-gray-600">
                  No need to research investments, monitor markets, or make tactical adjustments. 
                  We handle all the day-to-day portfolio management tasks.
                </p>
              </div>
              <div className="space-y-4">
                <h4 className="text-xl font-semibold text-primary">Risk Management</h4>
                <p className="text-gray-600">
                  Systematic approach to risk management with regular portfolio rebalancing and 
                  diversification across multiple asset classes and sectors.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="bg-primary text-white rounded-2xl p-8 md:p-12"
          >
            <div className="text-center">
              <h2 className="text-3xl font-heading font-bold mb-6">Start Professional Portfolio Management</h2>
              <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
                Let our experienced portfolio managers take care of your investments while you focus on 
                achieving your life goals. Contact us to learn more about our managed portfolio options.
              </p>
              <a 
                href="/contact-us" 
                className="btn btn-secondary text-lg py-4 px-8 shadow-xl hover:shadow-secondary/20 btn-3d inline-block"
              >
                Explore Portfolio Options
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default ManagedPortfoliosPage; 